// Shared validation utilities for portfolio configuration

interface ValidationRule {
  type?: "string" | "number" | "boolean" | "object" | "array" | "percent";
  min?: number;
  max?: number;
  min_length?: number;
  max_length?: number;
  required_fields?: string[];
  properties?: Record<string, ValidationRule>;
  enum?: any[];
  pattern?: string;
  total_must_equal?: number;
  tolerance?: number;
}

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// Comprehensive validation function matching backend portfolio_config_service.py logic
export const validateFieldValue = (value: any, rules?: ValidationRule | null, allowPartialInput = false): ValidationResult => {
  if (!rules) return { isValid: true };

  try {
    // For partial input during typing, be more lenient
    if (allowPartialInput && typeof value === 'string') {
      // Allow empty strings and partial numbers during typing
      if (value === '' || value === '0' || value === '0.' || /^\d*\.?\d*$/.test(value)) {
        return { isValid: true };
      }
    }

    // Type validation - exact match to backend logic
    if (rules.type) {
      const expectedType = rules.type;
      
      if (expectedType === "number") {
        // Accept both number and string that can be parsed as number
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (typeof numValue !== "number" || isNaN(numValue)) {
          return { isValid: false, error: "Value must be a number" };
        }
      } else if (expectedType === "percent") {
        // Accept both number and string that can be parsed as number for percentages
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (typeof numValue !== "number" || isNaN(numValue)) {
          return { isValid: false, error: "Value must be a percentage (number between 0 and 100)" };
        }
      } else if (expectedType === "string" && typeof value !== "string") {
        return { isValid: false, error: "Value must be a string" };
      } else if (expectedType === "boolean" && typeof value !== "boolean") {
        return { isValid: false, error: "Value must be a boolean" };
      } else if (expectedType === "object" && (typeof value !== "object" || value === null || Array.isArray(value))) {
        return { isValid: false, error: "Value must be an object" };
      } else if (expectedType === "array" && !Array.isArray(value)) {
        return { isValid: false, error: "Value must be an array" };
      }
    }

    // Range validation for numbers and percentages
    if (typeof value === "number" || (typeof value === "string" && !isNaN(parseFloat(value)))) {
      const numValue = typeof value === "string" ? parseFloat(value) : value;
      
      // For percent type, validate against whole number range (0-100)
      if (rules.type === "percent") {
        const minVal = rules.min !== undefined ? rules.min : 0;
        const maxVal = rules.max !== undefined ? rules.max : 100;
        
        if (numValue < minVal) {
          return { isValid: false, error: `Percentage must be at least ${minVal}%` };
        }
        if (numValue > maxVal) {
          return { isValid: false, error: `Percentage must be at most ${maxVal}%` };
        }
      } else {
        // Regular number validation
        if (rules.min !== undefined && numValue < rules.min) {
          return { isValid: false, error: `Value must be at least ${rules.min}` };
        }
        if (rules.max !== undefined && numValue > rules.max) {
          return { isValid: false, error: `Value must be at most ${rules.max}` };
        }
      }
    }

    // Length validation for strings and arrays
    if (typeof value === "string" || Array.isArray(value)) {
      if (rules.min_length !== undefined && value.length < rules.min_length) {
        return { isValid: false, error: `Length must be at least ${rules.min_length}` };
      }
      if (rules.max_length !== undefined && value.length > rules.max_length) {
        return { isValid: false, error: `Length must be at most ${rules.max_length}` };
      }
    }

    // Required fields validation for objects
    if (typeof value === "object" && value !== null && !Array.isArray(value) && rules.required_fields) {
      const missingFields = rules.required_fields.filter(field => !(field in value));
      if (missingFields.length > 0) {
        return { isValid: false, error: `Missing required fields: ${missingFields.join(", ")}` };
      }
    }

    // Enum validation
    if (rules.enum && rules.enum.length > 0 && !rules.enum.includes(value)) {
      return { isValid: false, error: `Value must be one of: ${rules.enum.join(", ")}` };
    }

    // Pattern validation for strings
    if (rules.pattern && typeof value === "string") {
      try {
        const regex = new RegExp(rules.pattern);
        if (!regex.test(value)) {
          return { isValid: false, error: "Value does not match required pattern" };
        }
      } catch (regexError) {
        return { isValid: false, error: "Invalid validation pattern" };
      }
    }

    // Total sum validation for objects - matches backend logic exactly
    if (typeof value === "object" && value !== null && !Array.isArray(value) && rules.total_must_equal !== undefined) {
      const expectedTotal = rules.total_must_equal;
      const actualTotal = Object.values(value).reduce((sum: number, v) => {
        if (typeof v === "number") {
          return sum + v;
        } else if (typeof v === "string") {
          const numValue = parseFloat(v);
          return sum + (isNaN(numValue) ? 0 : numValue);
        }
        return sum;
      }, 0);
      const tolerance = rules.tolerance !== undefined ? rules.tolerance : 0.01;

      if (Math.abs(actualTotal - expectedTotal) > tolerance) {
        return { isValid: false, error: `Values must sum to ${expectedTotal}, but sum is ${actualTotal.toFixed(2)}` };
      }
    }

    return { isValid: true };
  } catch (error) {
    return { isValid: false, error: `Validation error: ${error}` };
  }
};

// Validate a nested object field using property-specific rules
export const validateNestedField = (fieldKey: string, fieldValue: any, parentRules?: ValidationRule | null): ValidationResult => {
  if (!parentRules?.properties?.[fieldKey]) {
    return { isValid: true };
  }
  
  const fieldRules = parentRules.properties[fieldKey];
  return validateFieldValue(fieldValue, fieldRules);
};

// Validate an entire object against its validation rules
export const validateObjectField = (objectValue: any, rules?: ValidationRule | null): ValidationResult => {
  if (!rules) return { isValid: true };
  
  // First validate the object itself
  const objectValidation = validateFieldValue(objectValue, rules);
  if (!objectValidation.isValid) {
    return objectValidation;
  }
  
  // Then validate each property if it has specific rules
  if (typeof objectValue === "object" && objectValue !== null && !Array.isArray(objectValue) && rules.properties) {
    for (const [fieldKey, fieldValue] of Object.entries(objectValue)) {
      const fieldValidation = validateNestedField(fieldKey, fieldValue, rules);
      if (!fieldValidation.isValid) {
        return {
          isValid: false,
          error: `${fieldKey}: ${fieldValidation.error}`
        };
      }
    }
  }
  
  return { isValid: true };
};

export type { ValidationRule, ValidationResult };
